import { useRef, useState } from 'react';
import { Box, IconButton, Stack, TextField, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import { COLORS } from '../../../utils/Application_Constants';
import usePatchProcessedTimesheet from '../../hooks/usePatchProcessedTimesheet';
import useToast from '../../../utils/useToast';

const FONT_STYLES = { fontSize: '12px', fontWeight: 400, lineHeight: 'normal' };
const ACTION_BUTTON_STYLES = {
    borderRadius: '4px',
    border: `1px solid ${COLORS.LT_MIDNIGHT10}`,
    width: '21px',
    height: '21px',
    backgroundColor: COLORS.LT_MIDNIGHT5,
    '&:hover': { backgroundColor: COLORS.LT_MIDNIGHT10 },
    '&:disabled': {
        backgroundColor: COLORS.LT_MIDNIGHT5,
        border: `1px solid ${COLORS.LT_MIDNIGHT5}`,
    },
};

const UpdateAccountingCaseInput = ({ processedTimesheetId, initialValue }) => {
    const initialValueRef = useRef(initialValue ?? '');
    const { renderToast, showSuccessToast, showErrorToast } = useToast();
    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(initialValueRef.current);
    const [error, setError] = useState('');

    const { mutate, isLoading } = usePatchProcessedTimesheet({
        onSuccess: () => {
            setIsEditing(false);
            setError('');
            initialValueRef.current = value;
            showSuccessToast('Accounting Case updated successfully!');
        },
        onError: (apiError) => {
            showErrorToast(
                apiError?.message || 'Updating accounting case failed. Please try again'
            );
        },
    });

    const handleCaseNumberChange = (e) => {
        const newValue = e.target.value;
        if (newValue.length < 6) {
            setError('Please enter a 6 digit case number');
        } else if (newValue.length > 32) {
            setError('Case number must be less than 32 digits');
        } else {
            setError('');
        }
        setValue(newValue);
    };

    const handleSubmit = () => {
        mutate({ processedTimesheetId, accountingCase: value });
    };

    const cancelEditing = () => {
        setIsEditing(false);
        setValue(initialValueRef.current);
        setError('');
    };

    const handleBlur = () => {
        if (value === initialValueRef.current) cancelEditing();
    };

    return (
        <>
            {renderToast()}
            {!isEditing ? (
                <Stack direction="row" alignItems="center" gap={1}>
                    <Typography
                        sx={{
                            ...FONT_STYLES,
                            cursor: value ? 'text' : 'pointer',
                            color: value ? COLORS.LT_MIDNIGHT75 : COLORS.LT_MIDNIGHT25,
                        }}
                        onClick={() => setIsEditing(true)}
                    >
                        {value || 'Add case number'}
                    </Typography>
                    <IconButton onClick={() => setIsEditing(true)} sx={{ p: 0 }}>
                        <EditIcon sx={{ fontSize: 12, color: COLORS.LT_JOURNEY }} />
                    </IconButton>
                </Stack>
            ) : (
                <Box position="relative">
                    <TextField
                        autoFocus
                        variant="outlined"
                        fullWidth
                        size="small"
                        margin="dense"
                        placeholder="Add case number"
                        value={value}
                        onChange={handleCaseNumberChange}
                        onBlur={handleBlur}
                        disabled={isLoading}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !isLoading) handleSubmit();
                        }}
                        sx={({ palette }) => ({
                            my: 0,
                            '& .MuiInputBase-root.MuiOutlinedInput-root': {
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderRadius: '4px',
                                    borderColor: COLORS.LT_MIDNIGHT10,
                                },
                                '&.Mui-focused, &:hover': {
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: palette.primary.main,
                                    },
                                },
                                '&.Mui-error': {
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: palette.error.main,
                                    },
                                },
                            },
                            '& input': {
                                ...FONT_STYLES,
                                p: '4px !important',
                                color: COLORS.LT_MIDNIGHT75,
                            },
                        })}
                        slotProps={{ formHelperText: { sx: { ...FONT_STYLES, mx: 0.5, mt: 1 } } }}
                        helperText={error}
                        error={Boolean(error)}
                    />
                    <Box position="absolute" top={27} right={2} zIndex={1} display="flex" gap={0.5}>
                        <IconButton
                            color="success"
                            sx={ACTION_BUTTON_STYLES}
                            disabled={
                                Boolean(error) || value === initialValueRef.current || isLoading
                            }
                            loading={isLoading}
                            onClick={handleSubmit}
                        >
                            <CheckIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton
                            color="error"
                            sx={ACTION_BUTTON_STYLES}
                            disabled={isLoading}
                            onClick={cancelEditing}
                        >
                            <CancelIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Box>
                </Box>
            )}
        </>
    );
};

export default UpdateAccountingCaseInput;
